/**
 * The menu editor's draft, as pure functions — a form with a reorderable list, a partial
 * patch and a create that becomes an edit has more state transitions than a mounted test
 * can comfortably reach.
 *
 * TWO SHAPES:
 *   • THE DRAFT binds to the form. Every option carries a local `key` (never sent) so `v-for`
 *     survives a reorder, and the STORED `id` (null for a new option) that a WhatsApp tap
 *     carries back — the BFF mints ids for options that have none.
 *   • THE WIRE SHAPE is what `routes/api/menus.ts` validates: trimmed strings and `taskId`.
 *
 * Every number below is WhatsApp's, restated from `services/ai-sidecar/src/menus/limits.ts`;
 * the BFF and `/v1/menus` enforce the same ones.
 */
import { menuOptionMissing } from './missing-language.js';

/**
 * Move a row within a list, or return the list untouched when it cannot move.
 *
 * IT LIVES HERE NOW, and it was `task-draft.js`'s until that module went with the free-text
 * task editor. `components/MenuOptionList.vue` is its only caller, and it reached it through
 * a re-export from this file — so the function moved to where the re-export already said it
 * belonged rather than leaving one helper alive to hold one function.
 *
 * RETURNS THE SAME ARRAY, not a copy, when the move is impossible: the caller emits it
 * straight back up as `update:modelValue`, and a fresh array for a no-op would mark the
 * editor dirty for a button press that changed nothing.
 */
export const moveRow = (rows, index, delta) => {
  const target = index + delta;
  if (index < 0 || index >= rows.length) return rows;
  if (target < 0 || target >= rows.length) return rows;
  const next = [...rows];
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row);
  return next;
};

let nextOptionKey = 0;
const optionKey = () => {
  nextOptionKey += 1;
  return nextOptionKey;
};

export const MAX_OPTIONS = 10;
export const BUTTONS_UP_TO = 3;
export const LIMITS = Object.freeze({
  TITLE: 80,
  WHEN: 500,
  BODY: 1024,
  BUTTON: 20,
  OPTION: 24,
  BUTTON_OPTION: 20,
});

/**
 * What WhatsApp shows on the list button when the owner leaves it empty. Mirrors
 * `DEFAULT_LIST_BUTTON` in `services/ai-sidecar/src/menus/render-choices.ts` — the preview must
 * show the label the customer will really see. Kuwaiti, like the chat it sits under.
 */
export const DEFAULT_LIST_BUTTON = Object.freeze({
  en: 'Choose an option',
  ar: 'اختار من القائمة',
});

/** Three options or fewer are reply buttons (20 characters); four or more a list (24). */
export const optionTitleLimit = count =>
  count <= BUTTONS_UP_TO ? LIMITS.BUTTON_OPTION : LIMITS.OPTION;

const text = value => (typeof value === 'string' ? value : '');

export const optionRow = (over = {}) => ({
  key: optionKey(),
  id: null,
  titleEn: '',
  titleAr: '',
  taskId: null,
  ...over,
});

/** A brand-new menu opens with ONE empty option: a menu with none is not a menu. */
export const blankDraft = () => ({
  title: '',
  whenToShow: '',
  enabled: true,
  bodyEn: '',
  bodyAr: '',
  buttonEn: '',
  buttonAr: '',
  options: [optionRow()],
});

/** A saved menu, as the form edits it. */
export const toDraft = menu => ({
  title: text(menu?.title),
  whenToShow: text(menu?.whenToShow),
  enabled: menu?.enabled !== false,
  bodyEn: text(menu?.bodyEn),
  bodyAr: text(menu?.bodyAr),
  buttonEn: text(menu?.buttonEn),
  buttonAr: text(menu?.buttonAr),
  options: (Array.isArray(menu?.options) && menu.options.length
    ? menu.options
    : [{}]
  ).map(option =>
    optionRow({
      id: typeof option?.id === 'string' && option.id ? option.id : null,
      titleEn: text(option?.titleEn),
      titleAr: text(option?.titleAr),
      taskId: Number.isInteger(option?.taskId) ? option.taskId : null,
    })
  ),
});

/** The draft as the BFF reads it. A new option goes WITHOUT `id`; the local `key` never goes. */
export const draftToWire = draft => ({
  title: text(draft.title).trim(),
  whenToShow: text(draft.whenToShow).trim(),
  enabled: draft.enabled !== false,
  bodyEn: text(draft.bodyEn).trim(),
  bodyAr: text(draft.bodyAr).trim(),
  buttonEn: text(draft.buttonEn).trim(),
  buttonAr: text(draft.buttonAr).trim(),
  options: (draft.options ?? []).map(option => ({
    ...(option.id ? { id: option.id } : {}),
    titleEn: text(option.titleEn).trim(),
    titleAr: text(option.titleAr).trim(),
    taskId: Number.isInteger(option.taskId) ? option.taskId : null,
  })),
});

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const SCALAR_KEYS = [
  'title',
  'whenToShow',
  'enabled',
  'bodyEn',
  'bodyAr',
  'buttonEn',
  'buttonAr',
];

/** Only the keys that changed — the list's switch must be able to send `{ enabled }` alone. */
export const menuPatch = (draft, savedWire) => {
  const wire = draftToWire(draft);
  const patch = {};
  SCALAR_KEYS.forEach(key => {
    if (wire[key] !== savedWire[key]) patch[key] = wire[key];
  });
  if (!same(wire.options, savedWire.options)) patch.options = wire.options;
  return patch;
};

/** The title a customer reading `lang` sees: that language, else the other — as the sidecar sends. */
export const titleIn = (option, lang) => {
  const en = text(option.titleEn).trim();
  const ar = text(option.titleAr).trim();
  return lang === 'ar' ? ar || en : en || ar;
};

/**
 * Words a customer sends to stop or restart WhatsApp messages, never a menu option's title
 * — the sidecar's outbound sender reads exactly these, case- and diacritic-insensitively, as
 * an instruction rather than an ordinary reply (source of truth:
 * `services/ai-sidecar/src/outbound/keywords.ts`, repo `waslo-services`). An option titled
 * "Stop" would look, to that classifier, indistinguishable from an opt-out.
 */
const STOP_WORDS = new Set([
  'stop',
  'unsubscribe',
  // The words on a marketing template's opt-out button (ease C1, services spec P-4.4).
  'stop promotions',
  'إيقاف',
  'ايقاف',
  'توقف',
  'start',
  'unstop',
  'ابدأ',
  'ابدا',
]);

/** Trim, fold case, and drop Arabic diacritics/tatweel so "إيقاف" and "إيقاف" (with a
 * stray fatha) compare equal — a customer's keyboard adds those marks; we don't ask for
 * them back. */
const normalizeStopWord = value =>
  text(value)
    .trim()
    .toLowerCase()
    .replace(/[ً-ٟـ]/g, '');

const isStopWord = value => STOP_WORDS.has(normalizeStopWord(value));

const duplicateAt = options => {
  const seen = { en: new Set(), ar: new Set() };
  return options.findIndex(option => {
    const en = titleIn(option, 'en').toLowerCase();
    const ar = titleIn(option, 'ar').toLowerCase();
    const clash = seen.en.has(en) || seen.ar.has(ar);
    seen.en.add(en);
    seen.ar.add(ar);
    return clash;
  });
};

/**
 * Every rule the BFF enforces, checked here first so the owner reads it under the control
 * rather than as a banner after a round trip. Keyed by the form's fields.
 *
 * @param {object} draft
 * @param {(key: string, params?: object) => string} t
 */
export const validateDraft = (draft, t, languages = 'both') => {
  const wire = draftToWire(draft);
  const errors = {};
  const key = name => `WASLO.MENUS.EDITOR.ERRORS.${name}`;

  if (!wire.title) errors.title = t(key('TITLE'));
  else if (wire.title.length > LIMITS.TITLE)
    errors.title = t(key('TITLE_LONG'));

  if (!wire.whenToShow) errors.whenToShow = t(key('WHEN'));
  else if (wire.whenToShow.length > LIMITS.WHEN) {
    errors.whenToShow = t(key('WHEN_LONG'));
  }

  const count = wire.options.length;
  const limit = optionTitleLimit(count);
  const blank = wire.options.findIndex(o => !o.titleEn && !o.titleAr);
  const long = wire.options.findIndex(
    o => o.titleEn.length > limit || o.titleAr.length > limit
  );
  if (count === 0) errors.options = t(key('OPTIONS_EMPTY'));
  else if (count > MAX_OPTIONS) errors.options = t(key('OPTIONS_MANY'));
  else if (blank !== -1) {
    errors.options = t(key('OPTION_BLANK'), { number: blank + 1 });
  } else if (menuOptionMissing(wire.options, languages) !== -1) {
    // P-2.10: one language, and this option has no words in it (`menus/languages.ts`).
    errors.options = t(key(`OPTION_MISSING_${languages.toUpperCase()}`), {
      number: menuOptionMissing(wire.options, languages) + 1,
    });
  } else if (long !== -1) {
    errors.options = t(key('OPTION_LONG'), { number: long + 1, limit });
  } else {
    const stopWord = wire.options.findIndex(
      o => isStopWord(o.titleEn) || isStopWord(o.titleAr)
    );
    if (stopWord !== -1) {
      errors.options = t('WASLO.MENUS.EDITOR.OPTIONS.STOP_WORD');
    } else {
      const dup = duplicateAt(wire.options);
      if (dup !== -1) {
        errors.options = t(key('OPTION_DUPLICATE'), { number: dup + 1 });
      }
    }
  }

  ['bodyEn', 'bodyAr'].forEach(field => {
    if (wire[field].length > LIMITS.BODY) errors[field] = t(key('BODY_LONG'));
  });
  ['buttonEn', 'buttonAr'].forEach(field => {
    if (wire[field].length > LIMITS.BUTTON) {
      errors[field] = t(key('BUTTON_LONG'));
    }
  });
  return errors;
};

/**
 * Everything the WhatsApp preview draws, so `MenuPreview.vue` only renders. `over` marks a
 * title WhatsApp would refuse; `borrowed` one shown from the OTHER language because this one
 * is empty — exactly the fallback the sidecar applies to row titles when it sends. Option
 * titles still borrow the other language; the body and the list button do NOT (W-11), and
 * for two different reasons: the list button's empty box shows the sidecar's built-in button
 * text in THIS language (what it actually sends); the body's empty box shows the PLACEHOLDER
 * for the assistant's own reply, because the sidecar uses that reply itself as the body and
 * only falls back to its built-in body text when the reply is too long to carry the menu.
 */
export const previewModel = (draft, lang) => {
  const wire = draftToWire(draft);
  const count = wire.options.length;
  const limit = optionTitleLimit(count);
  const rows = wire.options.map(option => {
    const title = titleIn(option, lang);
    const own = lang === 'ar' ? option.titleAr : option.titleEn;
    return {
      title,
      length: title.length,
      over: title.length > limit,
      borrowed: title !== '' && own === '',
    };
  });
  // Neither borrows the other language (W-11), but not for the same reason. The list
  // button's empty box shows the sidecar's built-in button text in THIS language — exactly
  // what it sends. The body's empty box shows the PLACEHOLDER for the assistant's own reply:
  // the sidecar puts that reply itself above the menu and only falls back to its built-in
  // body text when the reply is too long to carry the menu, so an empty box here is not
  // "what the sidecar sends" the way the button is. Row titles still borrow the other
  // language.
  const ownButton = lang === 'ar' ? wire.buttonAr : wire.buttonEn;
  const button = ownButton || DEFAULT_LIST_BUTTON[lang === 'ar' ? 'ar' : 'en'];
  const body = lang === 'ar' ? wire.bodyAr : wire.bodyEn;
  return {
    kind: count <= BUTTONS_UP_TO ? 'buttons' : 'list',
    dir: lang === 'ar' ? 'rtl' : 'ltr',
    limit,
    rows,
    button,
    buttonOver: button.length > LIMITS.BUTTON,
    body,
    bodyOver: body.length > LIMITS.BODY,
  };
};

export default toDraft;
